/**
 * Socket Store - Central WebSocket Connection Management
 *
 * ARCHITECTURE: Two Independent Event Channels
 * =============================================
 *
 * This store manages the WebSocket connection that supports TWO separate event channels:
 *
 * 1. LAB EVENTS (for sidebar/model updates):
 *    socketConnection → socketStore.handleLabEvent() → modelStore
 *
 *    These events update model status, operation progress, and attention indicators
 *    in the sidebar and model details. This channel operates INDEPENDENTLY of
 *    Lab Partner visibility.
 *
 * 2. CHAT STREAMS (for Lab Partner AI):
 *    socketConnection → streamProcessor → chatStore
 *
 *    These events handle Lab Partner chat streaming responses.
 *
 * IMPORTANT: Lab events must NEVER depend on Lab Partner visibility.
 * The socket connection is initialized by App.tsx → useSocket() hook,
 * not by Lab Partner components. Components that only need to read
 * connectionStatus should access it directly via useSocketStore selector,
 * not via useSocket() hook (which has initialization side effects).
 *
 * Socket logging can be enabled via isAreaEnabled('socket') for debugging.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import mitt, { Emitter } from 'mitt';
import { socketConnection } from '../services/socket/socketConnection';
import { useModelStore } from './modelStore';
import { useUserStore } from './userStore';
import { createApiClient } from '../services/api/client';
import { ModelService } from '../services/api/model';
import { LabService } from '../services/api/lab';
import { DomainService } from '../services/api/domain';
import { UserService } from '../services/api/user';
import { ProfileService } from '../services/api/profile';
import { AccountService } from '../services/api/account';
import { SubscriptionService } from '../services/api/subscription';
import { ContentService } from '../services/api/content';
import { CtdEnsembleService } from '../services/api/ctdEnsemble';
import { PerformanceService } from '../services/api/performance';
import { EPickListService } from '../services/api/ePickList';
import { SupportService } from '../services/api/support';
import { getLogger, isAreaEnabled } from '../utils/logger';
import {
  ConnectionStatus,
  SocketEvent,
  TypedLabEvent,
  LabEventType,
  LabSubscriptionResponse,
  EventHistoryItem,
  ModelOperationProgressData,
  ModelOperationResultData,
  ModelPublishedData,
  ModelVisibilityChangeData,
  LpEventCountKey,
  LabPartnerStreamMessage,
} from '../services/socket/types';
import { customAlphabet } from 'nanoid';

const nanoid = customAlphabet('0123456789abcdefghijklmnopqrstuvWXYZ-', 21);
const logger = getLogger('socket');

/**
 * Helper function to create API services matching the useApi() pattern
 * This ensures consistency with how API services are used throughout the app
 */
const getApiServices = () => {
  const getValidToken = useUserStore.getState().getValidToken;
  const apiClient = createApiClient(getValidToken);

  // Return the SAME structure as useApi() hook
  return {
    model: ModelService(apiClient.modelApi),
    lab: LabService(apiClient.modelApi),
    domain: DomainService(apiClient.domainApi),
    user: UserService(apiClient.identityApi),
    profile: ProfileService(apiClient.identityApi),
    account: AccountService(apiClient.identityApi),
    subscription: SubscriptionService(apiClient.identityApi),
    content: ContentService(apiClient.contentApi),
    ctdEnsemble: CtdEnsembleService(apiClient.modelApi),
    performance: PerformanceService(apiClient.modelApi),
    ePickList: EPickListService(apiClient.modelApi),
    support: SupportService(apiClient.identityApi),
  };
};

// Event emitter for other stores to subscribe to
type SocketEvents = {
  ModelOperationProgress: TypedLabEvent;
  ModelOperationResult: TypedLabEvent;
  ModelPublished: TypedLabEvent;
  ModelVisibilityChange: TypedLabEvent;
};

export const socketEvents: Emitter<SocketEvents> = mitt<SocketEvents>();

// Stable references so mitt .off() actually works (requires same reference as .on())
const socketHandlers = {
  status: (event: SocketEvent) => useSocketStore.getState().handleConnectionStatus(event),
  subscription: (event: SocketEvent) => useSocketStore.getState().handleSubscriptionConfirmed(event),
  labEvent: (event: SocketEvent) => useSocketStore.getState().handleLabEvent(event),
  error: (event: SocketEvent) => useSocketStore.getState().handleError(event),
  eject: (event: SocketEvent) => useSocketStore.getState().handleEject(event),
  labPartnerStream: (event: SocketEvent) => useSocketStore.getState().handleLabPartnerStream(event),
};

const initialLpEventCounts: Record<LpEventCountKey, number> = {
  model_action: 0,
  feature_add: 0,
  feature_edit: 0,
  feature_remove: 0,
  progress: 0,
  done: 0,
  error: 0,
};

interface SocketStoreState {
  // Connection state
  connectionStatus: ConnectionStatus;
  isInitialized: boolean;
  lastError: string | null;
  initializationPromise: Promise<void> | null;

  // Lab subscription state
  subscribedLabId: string | null;

  // Event tracking for debugging
  eventHistory: EventHistoryItem[];
  eventCounts: Record<LabEventType, number>;

  // LP event tracking (only active when debug panel visible)
  lpEventCounts: Record<LpEventCountKey, number>;
  lpTrackingEnabled: boolean;

  // Ping/pong health
  pingHealth: {
    lastPongReceived: number;
    missedPongs: number;
    isHealthy: boolean;
  };
  healthInterval?: NodeJS.Timeout;
}

interface SocketStoreActions {
  // Initialization
  initialize: () => Promise<void>;
  cleanup: () => void;

  // Connection management
  reconnect: () => Promise<void>;

  // Lab subscription
  subscribeLab: (labId: string) => Promise<void>;
  unsubscribeLab: () => Promise<void>;

  // Event handlers
  handleConnectionStatus: (event: SocketEvent) => void;
  handleSubscriptionConfirmed: (event: SocketEvent) => void;
  handleLabEvent: (event: SocketEvent) => void;
  handleError: (event: SocketEvent) => void;
  handleEject: (event: SocketEvent) => void;

  // LP event tracking
  handleLabPartnerStream: (event: SocketEvent) => void;
  setLpTracking: (enabled: boolean) => void;

  // Debug controls
  clearEventHistory: () => void;
  clearLpEventCounts: () => void;
  updatePingHealth: () => void;

  // Selectors
  getConnectionStatus: () => ConnectionStatus;
  isConnected: () => boolean;
  getEventHistory: () => EventHistoryItem[];
  getEventCounts: () => Record<LabEventType, number>;
}

type SocketStore = SocketStoreState & SocketStoreActions;

const MAX_EVENT_HISTORY = 50;

const initialState: SocketStoreState = {
  connectionStatus: 'disconnected',
  isInitialized: false,
  lastError: null,
  initializationPromise: null,
  subscribedLabId: null,
  eventHistory: [],
  eventCounts: {
    ModelOperationProgress: 0,
    ModelOperationResult: 0,
    ModelPublished: 0,
    ModelVisibilityChange: 0,
    ModelDeleted: 0,
    ModelCreated: 0,
    ModelVersionCreated: 0,
    ModelVersionDeleted: 0,
    CheckpointCreated: 0,
  },
  lpEventCounts: { ...initialLpEventCounts },
  lpTrackingEnabled: false,
  pingHealth: {
    lastPongReceived: Date.now(),
    missedPongs: 0,
    isHealthy: true,
  },
};

export const useSocketStore = create<SocketStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      initialize: async () => {
        const state = get();

        // Return existing initialization if in progress
        if (state.initializationPromise) {
          logger.info(' Initialization already in progress');
          return state.initializationPromise;
        }

        // Check if already initialized
        if (state.isInitialized) {
          logger.info(' Already initialized');
          return Promise.resolve();
        }

        // Create initialization promise
        const initPromise = (async () => {
          logger.info(' Initializing socket connection');

          // Set both flags atomically to prevent race conditions
          set({
            isInitialized: true,
            connectionStatus: 'connecting'
          });

          try {
            // Set up event listeners using stable refs so .off() matches
            socketConnection.on('status', socketHandlers.status);
            socketConnection.on('subscription', socketHandlers.subscription);
            socketConnection.on('labEvent', socketHandlers.labEvent);
            socketConnection.on('error', socketHandlers.error);
            socketConnection.on('eject', socketHandlers.eject);

            // Connect WebSocket
            await socketConnection.connect();

            // Update ping health every 5 seconds
            const healthInterval = setInterval(() => {
              get().updatePingHealth();
            }, 5000);

            // Store interval ID for cleanup
            set({ healthInterval });

            // Note: Lab subscription is handled by useSocket hook to prevent duplicates
          } catch (error) {
            logger.error(' Failed to initialize:', error);
            set({
              isInitialized: false,
              connectionStatus: 'error',
              lastError: error instanceof Error ? error.message : 'Unknown error'
            });
            throw error;
          } finally {
            // Clear initialization promise when done
            set({ initializationPromise: null });
          }
        })();

        // Store the promise to prevent concurrent initialization
        set({ initializationPromise: initPromise });
        return initPromise;
      },

      cleanup: () => {
        logger.info('🧹 Cleaning up socket store');

        // Clear health interval
        const state = get();
        if (state.healthInterval) {
          clearInterval(state.healthInterval);
        }

        // Remove event listeners before disconnect to prevent receiving events during cleanup
        socketConnection.off('status', socketHandlers.status);
        socketConnection.off('subscription', socketHandlers.subscription);
        socketConnection.off('labEvent', socketHandlers.labEvent);
        socketConnection.off('error', socketHandlers.error);
        socketConnection.off('eject', socketHandlers.eject);

        // Remove LP listener if active
        if (state.lpTrackingEnabled) {
          socketConnection.off('labPartnerStream', socketHandlers.labPartnerStream);
        }

        // Disconnect WebSocket (this will clear all timers in socketConnection)
        socketConnection.disconnect();

        // Clear mitt event emitter listeners
        socketEvents.all.clear();

        // Reset state (this will clear initializationPromise too)
        set(initialState);
      },

      reconnect: async () => {
        logger.info(' Reconnecting');
        set({ lastError: null });
        await socketConnection.reconnect();
      },

      subscribeLab: async (labId: string) => {
        const currentLabId = get().subscribedLabId;

        // Already subscribed to this lab
        if (currentLabId === labId) {
          logger.debug(' Already subscribed to lab:', labId);
          return;
        }

        // Switching labs - need to disconnect and reconnect for clean slate
        if (currentLabId && currentLabId !== labId) {
          logger.info(' Switching labs, performing clean reconnect:', {
            from: currentLabId,
            to: labId
          });

          // Reconnect with new lab (handles disconnect, reconnect, and subscribe)
          await socketConnection.reconnectForLabSwitch(labId);

          // Update store state
          set({ subscribedLabId: labId });
          return;
        }

        // Initial subscription (no previous lab)
        logger.info(' Subscribing to lab:', labId);
        set({ subscribedLabId: labId });
        await socketConnection.subscribeLab(labId);
      },

      unsubscribeLab: async () => {
        const labId = get().subscribedLabId;
        if (!labId) return;

        logger.info(' Unsubscribing from lab:', labId);
        socketConnection.unsubscribeLab();
        set({
          subscribedLabId: null,
        });
      },

      handleConnectionStatus: (event: SocketEvent) => {
        const data = event.data as { status: ConnectionStatus } | undefined;
        const status = data?.status;
        logger.info(' Connection status:', status);
        set({ connectionStatus: status });

        // Clear error on successful connection
        if (status === 'connected') {
          set({ lastError: null });
        }
      },

      handleSubscriptionConfirmed: (event: SocketEvent) => {
        const data = event.data as LabSubscriptionResponse;

        // Log subscription details
        logger.info('[Event] Lab Subscription Confirmed', {
          labId: get().subscribedLabId,
          models: data.subscribed,
          timestamp: new Date().toISOString(),
        });
      },

      handleLabEvent: (event: SocketEvent) => {
        const labEvent = event.data as TypedLabEvent;

        // Ignore events for models not in the current lab
        const { models } = useModelStore.getState();
        if (!models.has(labEvent.data.modelId)) {
          return;
        }

        const state = get();

        // Add to event history
        const historyItem: EventHistoryItem = {
          id: nanoid(),
          type: labEvent.type,
          timestamp: labEvent.timestamp,
          modelId: labEvent.data.modelId,
          rawData: labEvent.data,
        };

        const newHistory = [historyItem, ...state.eventHistory].slice(0, MAX_EVENT_HISTORY);

        // Update event counts
        const newCounts = {
          ...state.eventCounts,
          [labEvent.type]: (state.eventCounts[labEvent.type] || 0) + 1,
        };

        set({
          eventHistory: newHistory,
          eventCounts: newCounts,
        });

        // Structured console logging
        const logData: Record<string, string | number | undefined> = {
          type: labEvent.type,
          timestamp: labEvent.timestamp,
          modelId: labEvent.data.modelId,
        };

        // Add type-specific data and handle ModelOperationProgress
        switch (labEvent.type) {
          case 'ModelOperationProgress': {
            const data = labEvent.data as ModelOperationProgressData;
            logData.stage = data.stage;
            logData.percentComplete = data.percentComplete;
            logData.remaining = data.remaining;

            // Handle ModelOperationProgress by updating the model store
            const api = getApiServices();
            useModelStore.getState().handleModelOperationProgress(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelOperationProgress:', error);
              });

            break;
          }
          case 'ModelOperationResult': {
            const data = labEvent.data as ModelOperationResultData;
            logData.status = data.status;
            logData.checkpointId = data.checkpointId;
            logData.versionId = data.versionId;

            // Handle ModelOperationResult by reloading the model
            const api = getApiServices();
            useModelStore.getState().handleModelOperationResult(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelOperationResult:', error);
              });

            break;
          }
          case 'ModelCreated': {
            const data = labEvent.data as ModelOperationProgressData;

            // Handle ModelCreated by updating the model
            const api = getApiServices();
            useModelStore.getState().handleModelCreated(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelCreated:', error);
              });

            break;
          }
          case 'ModelDeleted': {
            const data = labEvent.data as ModelOperationProgressData;

            // Handle ModelDeleted by updating the model
            const api = getApiServices();
            useModelStore.getState().handleModelDeleted(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelDeleted:', error);
              });

            break;
          }
          case 'ModelPublished': {
            const data = labEvent.data as ModelPublishedData;
            logData.visibility = data.visibility;
            logData.versionId = data.versionId;
            logData.checkpointId = data.checkpointId;

            // Handle ModelPublished by updating the model
            const api = getApiServices();
            useModelStore.getState().handleModelPublished(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelPublished:', error);
              });

            break;
          }
          case 'ModelVisibilityChange': {
            const data = labEvent.data as ModelVisibilityChangeData;
            logData.visibility = data.visibility;

            // Handle ModelVisibilityChange by updating the model
            const api = getApiServices();
            useModelStore.getState().handleModelChange(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelPublished:', error);
              });

            break;
          }
          case 'ModelVersionCreated': {
            const data = labEvent.data as ModelOperationProgressData;

            // Handle ModelCreated by updating the model
            const api = getApiServices();
            useModelStore.getState().handleModelVersionCreated(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelVersionCreated:', error);
              });

            break;
          }
          case 'ModelVersionDeleted': {
            const data = labEvent.data as ModelOperationProgressData;

            // Handle ModelDeleted by updating the model
            const api = getApiServices();
            useModelStore.getState().handleModelVersionDeleted(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle ModelVersionDeleted:', error);
              });

            break;
          }

          case 'CheckpointCreated': {
            const data = labEvent.data as ModelOperationProgressData;

            // Handle CheckpointCreated by updating the model
            const api = getApiServices();
            useModelStore.getState().handleCheckpointCreated(api, data)
              .catch(error => {
                logger.error('[Socket] Failed to handle CheckpointCreated:', error);
              });

            break;
          }

        }

        logger.info('[Event]', logData);

        // If socket logging is enabled, log raw data too
        if (isAreaEnabled('socket')) {
          console.log('[Socket Event Debug] Raw data:', labEvent);
        }

        // Emit typed event for other stores (Phase 3 - not used yet)
        socketEvents.emit(labEvent.type as keyof SocketEvents, labEvent);
      },

      handleError: (event: SocketEvent) => {
        const data = event.data as { message?: string } | undefined;
        const errorMessage = data?.message || 'Unknown error';
        logger.error(' Error:', errorMessage);
        set({ lastError: errorMessage });
      },

      handleEject: (event: SocketEvent) => {
        const data = event.data as { status: string };
        logger.warn('User ejected:', data.status);

        // Store ejection reason BEFORE cleanup/reset (so notification can show)
        useUserStore.getState().setEjectionStatus(data.status);

        // Clear tokens and session
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Disconnect socket
        get().cleanup();

        // Reset user store (triggers redirect to login via auth state listener)
        useUserStore.getState().reset();
      },

      handleLabPartnerStream: (event: SocketEvent) => {
        const message = event.data as LabPartnerStreamMessage;

        // Skip text_delta — fires per token (hundreds of times) and thrashes the store/UI
        if (message.type === 'text_delta') return;

        const state = get();
        const newCounts = { ...state.lpEventCounts };

        switch (message.type) {
          case 'model_action':
          case 'progress':
          case 'done':
          case 'error':
            newCounts[message.type]++;
            break;
          case 'feature_action': {
            const actions = message.data.actions;
            if (actions) {
              for (const action of actions) {
                const key = `feature_${action.action}` as LpEventCountKey;
                newCounts[key]++;
              }
            }
            break;
          }
        }

        set({ lpEventCounts: newCounts });
      },

      setLpTracking: (enabled: boolean) => {
        const state = get();
        if (enabled === state.lpTrackingEnabled) return;

        if (enabled) {
          socketConnection.on('labPartnerStream', socketHandlers.labPartnerStream);
        } else {
          socketConnection.off('labPartnerStream', socketHandlers.labPartnerStream);
        }

        set({ lpTrackingEnabled: enabled });
      },

      clearEventHistory: () => {
        logger.info(' Clearing event history');
        set({
          eventHistory: [],
          eventCounts: {
            ModelOperationProgress: 0,
            ModelOperationResult: 0,
            ModelPublished: 0,
            ModelVisibilityChange: 0,
            ModelDeleted: 0,
            ModelCreated: 0,
            ModelVersionCreated: 0,
            ModelVersionDeleted: 0,
            CheckpointCreated: 0,
          },
          lpEventCounts: { ...initialLpEventCounts },
        });
      },

      clearLpEventCounts: () => {
        set({ lpEventCounts: { ...initialLpEventCounts } });
      },

      updatePingHealth: () => {
        const health = socketConnection.getPingHealth();
        // Update if any value has changed
        const currentHealth = get().pingHealth;
        if (
          currentHealth.isHealthy !== health.isHealthy ||
          currentHealth.missedPongs !== health.missedPongs ||
          currentHealth.lastPongReceived !== health.lastPongReceived
        ) {
          set({ pingHealth: health });
        }
      },

      // Selectors
      getConnectionStatus: () => get().connectionStatus,

      isConnected: () => get().connectionStatus === 'connected',

      getEventHistory: () => get().eventHistory,

      getEventCounts: () => get().eventCounts,
    }),
    {
      name: 'socket-store',
    }
  )
);